import React from 'react'
import ReactDOM from 'react-dom'

import auth, { normalizeUserInfo } from '~/services/auth'
import { getLocale } from '~/core/utils'
import API_URLS from '~/core/api-urls'
import { APP_NAMES, EXPIRED_SUBSCRIPTION_STATUSES } from '~/core/constants'

import AllPlansPage from './components/vpaas/all-plans-page'
import NoPermissionPage from './components/no-permission-page'
import App from './components/app'
import SubscriptionExpiredPage from './components/subscription-expired-page'

import { getProSubscription, setAPIInfo } from './services/api'
import { parseJwt, setFavicon } from './core/utils'

import analytics, { initAnalytics, assignIDs } from '~/services/analytics'
import { initTranslate } from '~/core/translate/translate'
import appConfig from './app-config'
import componentsConfig from './components-config'
import envData, { getEnvironmentData } from '~/core/env'

const initApp = async () => {
  await getEnvironmentData()
  await initTranslate(getLocale())

  const { referrer } = document

  if (referrer) {
    localStorage.setItem('standaloneReferrer', referrer)
  }

  const queryParams = new URLSearchParams(window.location.search)
  const {
    getAuthRedirectUrl,
    documentTitle,
    favIconUrl,
    getRedirectOnProSubscription,
    getLogoutUrl,
    name: appName
  } = appConfig.current
  const isVpaas = appName === APP_NAMES.VPAAS || null
  const userType = isVpaas ? analytics.USER_TYPE.VPAAS : analytics.USER_TYPE.STANDALONE
  const { SuccessPage, CancelPage } = componentsConfig.current

  document.title = documentTitle
  setFavicon(favIconUrl)

  const preselectPlan = queryParams.get('preselectPlan')

  if (preselectPlan && isVpaas) {
    const signUpWithJaaS = planId => {
      window.location = `${API_URLS.JAAS}signup?pricing_plan_id=${planId}`
    }
    ReactDOM.render(<AllPlansPage selectPlan={signUpWithJaaS} />, document.getElementById('root'))
    return
  }

  try {
    const publicKey = envData.stripeKey
    const token = queryParams.get('token')

    let authResult
    if (token) {
      authResult = { accessToken: token, userInfo: normalizeUserInfo(parseJwt(token)) }
    } else {
      authResult = await auth.init()
    }

    await setAPIInfo(authResult)
    initAnalytics({
      ...authResult.userInfo,
      userType
    })

    const { isAdmin, emailVerified } = authResult.userInfo

    if (!emailVerified) {
      window.location = getLogoutUrl()
      return
    }

    if (!isAdmin) {
      ReactDOM.render(<NoPermissionPage />, document.getElementById('root'))
      return
    }

    if (queryParams.get('cancel')) {
      ReactDOM.render(<CancelPage />, document.getElementById('root'))
      return
    }

    if (queryParams.get('success')) {
      ReactDOM.render(<SuccessPage />, document.getElementById('root'))
      return
    }

    try {
      const proSub = await getProSubscription()
      if (proSub) {
        if (EXPIRED_SUBSCRIPTION_STATUSES.includes(proSub.status)) {
          ReactDOM.render(<SubscriptionExpiredPage userInfo={authResult.userInfo} />, document.getElementById('root'))
        } else {
          window.location = getRedirectOnProSubscription()
        }

        return
      }
    } catch {
      // token might have expired or services might be down. To prevent rendering the plans selection we force-logout
      // so the user can restart flow with a fresh token
      window.location = getLogoutUrl()
    }

    const pricingPlanId = queryParams.get('pricingPlanId')
    ReactDOM.render(<App publicKey={publicKey} planId={pricingPlanId} />, document.getElementById('root'))
  } catch (err) {
    const deviceId = await assignIDs()

    auth.register({
      deviceId,
      redirectUri: getAuthRedirectUrl(),
      referrer,
      isVpaas
    })
  }
}

initApp()
